import React, { useEffect, useState } from 'react';
import { adminGetServices, adminSyncCapacity } from '../../api';

const s = {
    h1:    { fontSize: 22, fontWeight: 700, marginBottom: 4 },
    sub:   { color: '#6b7280', fontSize: 13, marginBottom: 20 },
    top:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16 },
    panel: { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,.06)', overflow: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
    th:    { textAlign: 'left', padding: '10px 12px', background: '#f9fafb', borderBottom: '2px solid #f3f4f6', color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: .5, whiteSpace: 'nowrap' },
    td:    { padding: '9px 12px', borderBottom: '1px solid #f9fafb', verticalAlign: 'middle' },
    sync:  { padding: '8px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
    note:  { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 12, color: '#1d4ed8' },
};

const ACTIVE_BADGE = (a) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700,
    background: a ? '#d1fae5' : '#f3f4f6', color: a ? '#065f46' : '#9ca3af',
});

const SORT_KEYS = {
    'ID': 'service_id', 'Category': 'category', 'Name': 'name',
    'Task Type': 'task_type', 'Gender': 'gender', 'Price/1k': 'price_per_1k',
    'Min': 'min_qty', 'Max (auto)': 'max_qty', 'Status': 'is_active',
};

export default function AdminServices() {
    const [services, setServices] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [syncing, setSyncing]   = useState(false);
    const [serviceEta, setServiceEta] = useState({});
    const [sortCol, setSortCol]   = useState('ID');
    const [sortDir, setSortDir]   = useState('asc');

    const handleSort = (col) => {
        if (!SORT_KEYS[col]) return;
        if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir('asc'); }
    };

    const load = () => {
        setLoading(true);
        adminGetServices().then(r => setServices(r.data)).catch(() => {}).finally(() => setLoading(false));
        fetch('/public/service-eta').then(r => r.json())
            .then(d => { if (d && d.eta) setServiceEta(d.eta); })
            .catch(() => {});
    };

    useEffect(load, []);

    const doSync = async () => {
        setSyncing(true);
        try {
            await adminSyncCapacity();
            alert('Capacity synced! max_qty updated for all services.');
            load();
        } catch (e) {
            alert(e.response?.data?.error || 'Sync failed');
        } finally {
            setSyncing(false);
        }
    };

    return (
        <div>
            <div style={s.top}>
                <div>
                    <h1 style={s.h1}>Services</h1>
                    <div style={s.sub}>Read-only view. To edit prices or services, use Order Manager.</div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={s.note}>Managed from Order Manager</span>
                    <button style={s.sync} onClick={doSync} disabled={syncing}>
                        {syncing ? 'Syncing...' : 'Sync Capacity'}
                    </button>
                </div>
            </div>

            {!loading && (
                <div style={{ padding: '10px 0 6px', fontSize: 13, color: '#6b7280' }}>
                    <b style={{ color: '#111827' }}>{services.length}</b> services total
                    &nbsp;·&nbsp;<b style={{ color: '#059669' }}>{services.filter(s => s.is_active).length}</b> active
                    &nbsp;·&nbsp;<b style={{ color: '#9ca3af' }}>{services.filter(s => !s.is_active).length}</b> inactive
                </div>
            )}

            <div style={s.panel}>
                {loading ? <p style={{ padding: 24, color: '#9ca3af' }}>Loading...</p> : (() => {
                    const key = SORT_KEYS[sortCol];
                    const sorted = [...services].sort((a, b) => {
                        const av = a[key], bv = b[key];
                        const cmp = typeof av === 'number' ? av - bv : String(av ?? '').localeCompare(String(bv ?? ''));
                        return sortDir === 'asc' ? cmp : -cmp;
                    });
                    return (
                    <div className="og-table-wrap">
                    <table style={s.table}>
                        <thead>
                            <tr>
                                {['ID','Category','Name','Task Type','Gender','Price/1k','Min','Max (auto)','Country','ETA','Speed','Queue','Status'].map(h => {
                                    const sortable = !!SORT_KEYS[h];
                                    const active = sortCol === h;
                                    return (
                                        <th key={h} style={{ ...s.th, cursor: sortable ? 'pointer' : 'default', userSelect: 'none',
                                            color: active ? '#4f46e5' : '#6b7280' }}
                                            onClick={() => handleSort(h)}>
                                            {h}{active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : (sortable ? ' ⇅' : '')}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map(svc => (
                                <tr key={svc.service_id} style={{ opacity: svc.is_active ? 1 : 0.5 }}>
                                    <td style={s.td}>{svc.service_id}</td>
                                    <td style={{ ...s.td, fontSize: 11, color: '#6366f1', fontWeight: 600, maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={svc.category}>{svc.category}</td>
                                    <td style={{ ...s.td, maxWidth: 200 }} title={svc.name}>{svc.name}</td>
                                    <td style={s.td}>{svc.task_type}</td>
                                    <td style={s.td}>{svc.gender}</td>
                                    <td style={{ ...s.td, fontWeight: 700, fontFamily: 'monospace' }}>
                                        {parseFloat(svc.price_per_1k).toFixed(2)}
                                    </td>
                                    <td style={s.td}>{svc.min_qty}</td>
                                    <td style={{ ...s.td, color: '#6b7280' }}>{svc.max_qty}</td>
                                    <td style={{ ...s.td, fontSize: 11, color: '#9ca3af' }}>
                                        {(svc.available_countries || ['IT']).join(', ')}
                                    </td>
                                    {(() => {
                                        const eta = serviceEta[svc.task_type];
                                        const rawMin = eta ? (eta.raw_min || eta.start_min || 0) : 0;
                                        const color = rawMin === 0 ? '#059669' : rawMin <= 30 ? '#d97706' : '#dc2626';
                                        const etaLabel = rawMin === 0 ? 'Now' : rawMin < 60 ? `${rawMin}m` : `${Math.round(rawMin/60*10)/10}h`;
                                        return (<>
                                            <td style={{ ...s.td, fontWeight: 600, fontFamily: 'monospace', color }}>
                                                {eta ? etaLabel : '—'}
                                            </td>
                                            <td style={{ ...s.td, fontFamily: 'monospace' }}>
                                                {eta ? `${Math.round(eta.speed_per_hour)}/h` : '—'}
                                            </td>
                                            <td style={{ ...s.td, fontFamily: 'monospace', color: eta && eta.queue_size > 0 ? '#dc2626' : '#9ca3af' }}>
                                                {eta ? eta.queue_size : 0}
                                            </td>
                                        </>);
                                    })()}
                                    <td style={s.td}>
                                        <span style={ACTIVE_BADGE(svc.is_active)}>
                                            {svc.is_active ? 'ON' : 'OFF'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                    );
                })()}
            </div>
        </div>
    );
}
